import type { VideoWall } from '@ghostscreen/contracts';
import { useTranslation } from 'react-i18next';
import { manifestLabel, wallStatus, wallStatusColor, wallStatusLabel } from './wallFormat';

type WallsListCardProps = {
  walls: VideoWall[];
  selectedId: string | null;
  onSelect: (wallId: string) => void;
  onNewWall: () => void;
};

function MiniGrid({
  rows,
  cols,
  accent,
}: {
  rows: number;
  cols: number;
  accent: boolean;
}) {
  const cells = rows * cols;
  return (
    <span
      className="grid w-10 shrink-0 gap-0.5"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      dir="ltr"
      aria-hidden
    >
      {Array.from({ length: cells }, (_, index) => (
        <span
          key={index}
          className="aspect-video rounded-[2px]"
          style={{
            background: accent ? 'var(--gs-accent-solid)' : 'var(--gs-line)',
          }}
        />
      ))}
    </span>
  );
}

export function WallsListCard({ walls, selectedId, onSelect, onNewWall }: WallsListCardProps) {
  const { t } = useTranslation('videoWalls');
  return (
    <article
      className="overflow-hidden rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div
        className="flex flex-wrap items-center gap-3 border-b px-[22px] py-[17px]"
        style={{ borderColor: 'var(--gs-line)' }}
      >
        <h3 className="m-0 text-base font-bold" style={{ color: 'var(--gs-ink)' }}>
          {t('list.title')}
        </h3>
        <button
          type="button"
          onClick={onNewWall}
          className="ms-auto text-sm font-bold"
          style={{ color: 'var(--gs-accent)' }}
        >
          {t('list.new')}
        </button>
      </div>

      {walls.length === 0 ? (
        <p className="m-0 px-[22px] py-4 text-sm" style={{ color: 'var(--gs-muted)' }}>
          {t('list.empty')}
        </p>
      ) : (
        walls.map((wall) => {
          const status = wallStatus(wall);
          const selected = wall.id === selectedId;
          const onAir = status === 'on_air';
          return (
            <button
              key={wall.id}
              type="button"
              onClick={() => onSelect(wall.id)}
              className="flex w-full items-center gap-[13px] border-b px-[22px] py-3.5 text-start last:border-b-0"
              style={{
                borderColor: 'var(--gs-line)',
                borderInlineStart: `3px solid ${selected ? 'var(--gs-accent-solid)' : 'transparent'}`,
                background: selected ? 'var(--gs-panel2)' : 'transparent',
                fontFamily: 'inherit',
              }}
            >
              <MiniGrid rows={wall.rows} cols={wall.cols} accent={onAir || selected} />
              <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <span className="truncate text-[14.5px] font-bold" style={{ color: 'var(--gs-ink)' }}>
                  {wall.name}
                </span>
                <span className="text-xs" style={{ color: 'var(--gs-muted)' }}>
                  {t('list.meta', {
                    cols: wall.cols,
                    rows: wall.rows,
                    mode: t(`modes.${wall.mode}.inline`),
                    manifest: manifestLabel(wall.activePublication),
                  })}
                </span>
              </span>
              <span
                className="shrink-0 text-[11.5px] font-bold uppercase tracking-[0.08em]"
                style={{ color: wallStatusColor(status) }}
              >
                {wallStatusLabel(status, wall.mode)}
              </span>
            </button>
          );
        })
      )}
    </article>
  );
}
